import React, { useMemo } from "react";
import { formatDayMonthWithWeekday } from "../../utils/formatDate";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import OverflowTooltip from "./OverflowTooltip";

type AllowedField =
  | "nuiteesDisponibles"
  | "tauxOccupation"
  | "nuiteesOccupees"
  | "nombreDeVisiteurs";

interface Row {
  label: string;
  editable: boolean;
  field: AllowedField;
}

interface WeekTableProps {
  week?: string;
  startDate: Date;
  rows: Row[];
  weekData: WeekData;
  onDataChange: (day: number, updatedDayData: DailyData) => void;
  renderVisitorCollapse?: (args: {
    dates: { date: Date; label: string }[];
    weekData: WeekData;
    onUpdateDay: (
      dayIndex: number,
      updater: (prev: DailyData) => DailyData
    ) => void;
  }) => React.ReactNode;
}

export interface DailyData {
  nuiteesDisponibles?: number;
  pourcentageVendues?: number;
  tauxOccupation?: string;
  nuiteesOccupees?: number;
  nombreDeVisiteurs?: number;
  visitorDetails?: VisitorDetail[];
}

export type WeekData = { [day: number]: DailyData };

export interface VisitorDetail {
  visitorTypeId: number;
  visitorSubTypeId?: number | null;
  visitorGroupTypeId?: number | null;
  count: number;
}

const WeekTableReal: React.FC<WeekTableProps> = ({
  week,
  startDate,
  rows,
  weekData,
  onDataChange,
  renderVisitorCollapse,
}) => {
  const formattedDates = useMemo(
    () =>
      Array.from({ length: 7 }, (_, i) => {
        const date = new Date(startDate);
        date.setDate(date.getDate() + i);
        return {
          date,
          label: formatDayMonthWithWeekday(date, "short"),
        };
      }),
    [startDate]
  );

  const handleDataChange = (
    dayIndex: number,
    field: AllowedField,
    value: number
  ) => {
    if (value < 0) {
      return;
    }

    const updatedDayData = {
      ...weekData[dayIndex],
      [field]: value,
    };

    if (
      updatedDayData.nuiteesOccupees !== undefined &&
      updatedDayData.nuiteesDisponibles !== undefined &&
      updatedDayData.nuiteesDisponibles > 0
    ) {
      const percentage = (
        (updatedDayData.nuiteesOccupees / updatedDayData.nuiteesDisponibles) *
        100
      ).toFixed(2);
      updatedDayData.pourcentageVendues = parseFloat(percentage);
      updatedDayData.tauxOccupation = `${percentage}%`;
    } else {
      updatedDayData.pourcentageVendues = undefined;
      updatedDayData.tauxOccupation = "-";
    }

    onDataChange(dayIndex, updatedDayData);
  };

  const onUpdateDay = (
    dayIndex: number,
    updater: (prev: DailyData) => DailyData
  ) => {
    const prev = weekData[dayIndex] || {};
    let updated = updater(prev);

    if (updated.visitorDetails && Array.isArray(updated.visitorDetails)) {
      const details = updated.visitorDetails;
      const hasGroupBySubType: Record<string, boolean> = {};
      const hasAnySubOrGroupByType: Record<number, boolean> = {};
      for (const v of details) {
        const subKey = String(v.visitorSubTypeId ?? "none");
        if (v.visitorGroupTypeId != null) {
          hasGroupBySubType[subKey] = true;
        } else if (!(subKey in hasGroupBySubType)) {
          hasGroupBySubType[subKey] = false;
        }
        const hasSubOrGroup =
          v.visitorSubTypeId != null || v.visitorGroupTypeId != null;
        if (hasSubOrGroup) hasAnySubOrGroupByType[v.visitorTypeId] = true;
        if (!(v.visitorTypeId in hasAnySubOrGroupByType))
          hasAnySubOrGroupByType[v.visitorTypeId] = false;
      }
      const sum = details.reduce((acc, v) => {
        const subKey = String(v.visitorSubTypeId ?? "none");
        const hasGroups = hasGroupBySubType[subKey];
        const isSubTypeAggregate =
          v.visitorSubTypeId != null && v.visitorGroupTypeId == null;
        if (hasGroups && isSubTypeAggregate) return acc;
        const isTypeAggregate =
          v.visitorSubTypeId == null && v.visitorGroupTypeId == null;
        if (isTypeAggregate && hasAnySubOrGroupByType[v.visitorTypeId])
          return acc;
        return acc + (Number(v.count) || 0);
      }, 0);
      updated = { ...updated, nombreDeVisiteurs: sum };
    }

    if (
      updated.nuiteesOccupees !== undefined &&
      updated.nuiteesDisponibles !== undefined &&
      updated.nuiteesDisponibles > 0
    ) {
      const percentage = (
        (updated.nuiteesOccupees / updated.nuiteesDisponibles) * 100
      ).toFixed(2);
      updated.pourcentageVendues = parseFloat(percentage);
      updated.tauxOccupation = `${percentage}%`;
    } else {
      updated.pourcentageVendues = undefined;
      updated.tauxOccupation = "-";
    }

    onDataChange(dayIndex, updated);
  };

  return (
    <Table
      sx={{
        width: "90%",
        mt: 3,
        boxShadow: 3,
        borderRadius: 2,
        tableLayout: "fixed",
      }}
    >
      {week && (
        <caption
          style={{
            captionSide: "top",
            textAlign: "left",
            fontSize: "1.25rem",
            color: "#bf2f38",
          }}
        >
          {week}
        </caption>
      )}
      <TableHead>
        <TableRow>
          <TableCell
            align="center"
            sx={{
              fontSize: "1rem",
              backgroundColor: "#f5f5f5",
              color: "#bf2f38",
            }}
          >
            Date
          </TableCell>
          {formattedDates.map(({ label }, i) => (
            <TableCell
              key={i}
              align="center"
              sx={{
                fontSize: "1rem",
                color: "#bf2f38",
                backgroundColor: "#fbeaec",
              }}
            >
              {label}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row, rowIndex) => (
          <React.Fragment key={rowIndex}>
            <TableRow
              sx={{ backgroundColor: rowIndex % 2 === 0 ? "#fafafa" : "#fff" }}
            >
              <TableCell sx={{ width: 260, whiteSpace: "pre-line", wordWrap: "break-word", textAlign: "left" }}>
                {row.label}
              </TableCell>
              {formattedDates.map((_, colIndex) => {
                const cellData = weekData[colIndex] || {};
                const value = cellData[row.field] ?? "";
                const isOverLimit =
                  row.field === "tauxOccupation" &&
                  cellData.pourcentageVendues !== undefined &&
                  cellData.pourcentageVendues > 100;

                return (
                  <TableCell
                    key={colIndex}
                    align="center"
                    sx={{
                      backgroundColor: isOverLimit ? "#ffebee" : "inherit",
                    }}
                  >
                    {row.editable ? (
                      <TextField
                        variant="outlined"
                        size="small"
                        type="number"
                        inputProps={{ min: 0 }}
                        value={value}
                        onChange={(e) =>
                          handleDataChange(
                            colIndex,
                            row.field,
                            Number(e.target.value)
                          )
                        }
                        sx={{
                          width: "80px",
                          "& .MuiInputBase-input": {
                            textAlign: "center",
                            padding: "6px 4px",
                            fontSize: "0.875rem",
                            color: isOverLimit ? "red" : "inherit",
                          },
                        }}
                      />
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{
                          color: isOverLimit ? "red" : "#222222",
                        }}
                      >
                        {cellData[row.field] !== undefined
                          ? cellData[row.field]
                          : "-"}
                      </Typography>
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
            {row.field === "nombreDeVisiteurs" && renderVisitorCollapse ? (
              <TableRow>
                <TableCell colSpan={formattedDates.length + 1} sx={{ p: 0 }}>
                  {renderVisitorCollapse({
                    dates: formattedDates,
                    weekData,
                    onUpdateDay,
                  })}
                </TableCell>
              </TableRow>
            ) : null}
          </React.Fragment>
        ))}
      </TableBody>
    </Table>
  );
};

export default WeekTableReal;
